import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { JournalEntryExternalLink } from '@cottage/design-system/internal-tooling/ui'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import WarningBanner from '@/components/WarningBanner'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import type { JournalEntryLine } from '@/services/accounting'
import type { BackgroundJob } from '@/services/jobs'
import { isBackgroundJobActive } from '@/services/jobs'
import {
  downloadBackfillAuditCsv,
  getBackfillAuditBounds,
  getBackfillAuditDetail,
  getBackfillAuditSummary,
  getCatchUpProposal,
  listBackfillAudit,
  qboJournalUrl,
  runBackfillAudit,
  updateBackfillAuditDecision,
  type BackfillAuditDecision,
  type BackfillAuditDetail,
  type BackfillAuditListItem,
  type BackfillAuditNormLine,
  type BackfillAuditSummary,
  type CatchUpProposal,
} from '@/services/cloverBackfillAudit'
import { getBackfillApplyMonths, type BackfillApplyMonthSummary } from '@/services/cloverBackfillApply'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import { filterRowsByText, filterRowsWithTree, toggleSingleColumnSort } from '../fixed-assets/fixedAssetsTableFilters'
import { FixedAssetsSortableHeader } from '../fixed-assets/FixedAssetsSortableHeader'
import {
  BACKFILL_AUDIT_PAGE_KEY,
  mergeBackfillAuditMonthSelectOptions,
  monthToDateRange,
  COLUMN_KEYS,
  COLUMN_LABELS,
  DEFAULT_SORT,
  DEFAULT_WIDTHS,
  FIELD_DEFINITIONS,
  PINNED_COLUMNS,
  buildBackfillAuditFilterFields,
  backfillAuditRunButtonLabel,
  decisionSelectOptions,
  fixedColumnStyle,
  formatBackfillAccountingMoney,
  formatBackfillMoney,
  formatDiffSummary,
  isBackfillAuditColumnSortable,
  isBackfillAuditPinnedColumn,
  pinnedCellClass,
  pinnedHeaderClass,
  suggestedDecisionLabel,
  type BackfillAuditColumnKey,
} from './backfillAuditTableConfig'
import BackfillAuditLineComparison from './BackfillAuditLineComparison'
import CloverBackfillApplyCard from './CloverBackfillApplyCard'
import { useCloverJobSession } from './useCloverJobSession'
import { cloverJobStatusLabel } from './cloverTokenSync'

type Props = {
  canEdit: boolean
}

const PAGE_SIZE = 50
const FETCH_PAGE_SIZE = 500
const STORE_OPTIONS = [
  { id: 'River Oaks', label: 'River Oaks' },
  { id: 'Highland Park', label: 'Highland Park' },
]


function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  disabled?: boolean
}) {
  return (
    <div
      className="backfill-audit-segmented-toggle inline-flex rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
            value === opt.value
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function makePlaceholderJob(jobId: number, totalItems: number): BackgroundJob {
  return {
    id: jobId,
    job_type: 'clover_backfill_audit',
    status: 'pending',
    total_items: totalItems,
    processed_items: 0,
    successful_items: 0,
    failed_items: 0,
    created_at: new Date().toISOString(),
  }
}

function rowKey(row: BackfillAuditListItem): string {
  return `${row.store_name}|${row.report_date}`
}

function CodeTerm({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-800">
      {children}
    </code>
  )
}

function BackfillAuditDecisionGlossary() {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-gray-900">Action glossary</p>
      <ul className="list-disc space-y-1 pl-4 text-gray-700">
        <li>
          <CodeTerm>gift_card_only</CodeTerm> rows: <CodeTerm>auto</CodeTerm>
        </li>
        <li>
          <CodeTerm>manual_correction</CodeTerm>, <CodeTerm>deposit_mismatch</CodeTerm>,{' '}
          <CodeTerm>ar_classification</CodeTerm> → open the row, compare the two columns, decide{' '}
          <CodeTerm>by_hand</CodeTerm> or <CodeTerm>auto</CodeTerm>
        </li>
        <li>
          <CodeTerm>deposit_window</CodeTerm> → leave <CodeTerm>needs_code_change</CodeTerm> and read
          the summary&apos;s deposit-cutoff inference against{' '}
          <CodeTerm>DEPOSIT_REPORT_CUTOFF = 2026-01-13</CodeTerm>
        </li>
        <li>
          <CodeTerm>identical</CodeTerm> → <CodeTerm>skip</CodeTerm>
        </li>
      </ul>
    </div>
  )
}

function columnHeaderLabel(columnKey: BackfillAuditColumnKey): ReactNode {
  if (columnKey === 'decision') {
    return (
      <HoverHelp
        label={COLUMN_LABELS[columnKey]}
        aria-label={`${COLUMN_LABELS[columnKey]} column help`}
      >
        <BackfillAuditDecisionGlossary />
      </HoverHelp>
    )
  }
  return COLUMN_LABELS[columnKey]
}

function proposalLinesToJournalEntry(lines: CatchUpProposal['lines']): JournalEntryLine[] {
  return lines.map((line) => ({
    posting_type: line.posting_type,
    amount: line.amount,
    account_id: line.account_id,
    account_name: line.account_name,
    department_id: line.department_id ?? null,
    department_name: line.department ?? null,
    entity_id: line.entity_id ?? null,
    entity_name: line.entity ?? null,
    description: line.reason,
  }))
}

export default function CloverBackfillAudit({ canEdit }: Props) {
  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const serverById = useBackgroundJobsStore((s) => s.serverById)

  const [bounds, setBounds] = useState<Awaited<ReturnType<typeof getBackfillAuditBounds>> | null>(
    null,
  )
  const [auditMonth, setAuditMonth] = useState<string | null>(null)
  const [selectedStores, setSelectedStores] = useState<string[]>([])
  const [refreshRun, setRefreshRun] = useState(false)
  const [runJobId, setRunJobId] = useState<number | null>(null)
  const [auditStartedMonths, setAuditStartedMonths] = useState<Set<string>>(() => new Set())

  const [allItems, setAllItems] = useState<BackfillAuditListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [listLoading, setListLoading] = useState(false)
  const [summary, setSummary] = useState<BackfillAuditSummary | null>(null)
  const [applyMonths, setApplyMonths] = useState<BackfillApplyMonthSummary[]>([])

  const [textSearch, setTextSearch] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(DEFAULT_SORT)
  const [columnVisibility, setColumnVisibility] = useState(() =>
    loadColumnVisibility(BACKFILL_AUDIT_PAGE_KEY, FIELD_DEFINITIONS),
  )
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [detailByKey, setDetailByKey] = useState<Record<string, BackfillAuditDetail>>({})
  const [detailLoadingKey, setDetailLoadingKey] = useState<string | null>(null)
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({})
  const [isExporting, setIsExporting] = useState(false)

  const [catchUpOpen, setCatchUpOpen] = useState(false)
  const [catchUp, setCatchUp] = useState<CatchUpProposal | null>(null)
  const [catchUpLoading, setCatchUpLoading] = useState(false)

  const filterFields = useMemo(() => buildBackfillAuditFilterFields(), [])
  const loadApplyMonths = useCallback(async () => {
    try {
      const { months } = await getBackfillApplyMonths()
      setApplyMonths(months)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load apply months'))
    }
  }, [])
  const auditMonthOptions = useMemo(
    () => mergeBackfillAuditMonthSelectOptions(bounds?.updatable_through, applyMonths),
    [applyMonths, bounds?.updatable_through],
  )
  const { from: fromDate, to: toDate } = useMemo(
    () => (auditMonth ? monthToDateRange(auditMonth) : { from: '', to: '' }),
    [auditMonth],
  )
  const resolvedToDate = toDate || undefined

  const closedThrough = bounds?.closed_through ?? '2025-12-31'

  const activeGlobalJob = useMemo(() => {
    for (const { job } of Object.values(serverById)) {
      if (
        (job.job_type === 'clover_backfill_audit' || job.job_type === 'clover_backfill_apply') &&
        isBackgroundJobActive(job.status)
      ) {
        return job
      }
    }
    return null
  }, [serverById])

  const runJob = (runJobId ? serverById[runJobId]?.job : null) ?? activeGlobalJob
  const isJobRunning = runJob != null && isBackgroundJobActive(runJob.status)

  useCloverJobSession(runJob?.status ?? null)

  useEffect(() => {
    void getBackfillAuditBounds()
      .then((data) => {
        setBounds(data)
        if (data.updatable_through) {
          setAuditMonth((current) => current ?? data.updatable_through!.slice(0, 7))
        }
      })
      .catch((error) => {
        toast.error(formatApiError(error, 'Failed to load audit bounds'))
      })
  }, [])

  useEffect(() => {
    void loadApplyMonths()
  }, [loadApplyMonths])

  const loadList = useCallback(async () => {
    if (!fromDate || !resolvedToDate) return
    setListLoading(true)
    try {
      const first = await listBackfillAudit({
        from: fromDate,
        to: resolvedToDate,
        page: 1,
        page_size: FETCH_PAGE_SIZE,
      })
      const all = [...first.items]
      const totalCount = first.total
      const totalPages = Math.max(1, Math.ceil(totalCount / FETCH_PAGE_SIZE))
      for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
        const response = await listBackfillAudit({
          from: fromDate,
          to: resolvedToDate,
          page: currentPage,
          page_size: FETCH_PAGE_SIZE,
        })
        all.push(...response.items)
      }
      setAllItems(all)
      setTotal(totalCount)
      setPage(1)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load audit rows'))
      setAllItems([])
      setTotal(0)
    } finally {
      setListLoading(false)
    }
  }, [fromDate, resolvedToDate])

  const loadSummary = useCallback(async () => {
    if (!fromDate || !resolvedToDate) return
    try {
      const data = await getBackfillAuditSummary({ from: fromDate, to: resolvedToDate })
      setSummary(data)
    } catch {
      setSummary(null)
    }
  }, [fromDate, resolvedToDate])

  useEffect(() => {
    void loadList()
    void loadSummary()
  }, [loadList, loadSummary])

  useEffect(() => {
    if (!catchUpOpen || catchUp != null || catchUpLoading) return
    setCatchUpLoading(true)
    void getCatchUpProposal()
      .then(setCatchUp)
      .catch((error) => {
        toast.error(formatApiError(error, 'Failed to load catch-up proposal'))
      })
      .finally(() => setCatchUpLoading(false))
  }, [catchUp, catchUpLoading, catchUpOpen])

  useEffect(() => {
    if (!runJob || runJob.status !== 'completed') return
    void loadList()
    void loadSummary()
    if (catchUpOpen) {
      setCatchUp(null)
      setCatchUpLoading(true)
      void getCatchUpProposal()
        .then(setCatchUp)
        .finally(() => setCatchUpLoading(false))
    }
  }, [catchUpOpen, loadList, loadSummary, runJob])

  const handleRun = useCallback(async () => {
    if (!canEdit || !fromDate || !resolvedToDate || !auditMonth) return
    try {
      const result = await runBackfillAudit({
        store_names: selectedStores.length > 0 ? selectedStores : undefined,
        from: fromDate,
        to: resolvedToDate,
        refresh: refreshRun,
      })
      setAuditStartedMonths((current) => new Set(current).add(auditMonth))
      setRunJobId(result.job_id)
      const storeCount = selectedStores.length > 0 ? selectedStores.length : 2
      const dayCount =
        Math.round(
          (new Date(`${resolvedToDate}T00:00:00`).getTime() -
            new Date(`${fromDate}T00:00:00`).getTime()) /
            86_400_000,
        ) + 1
      registerServerJob(makePlaceholderJob(result.job_id, Math.max(dayCount, 0) * storeCount))
      toast.success('Backfill audit job started')
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to start audit job'))
    }
  }, [auditMonth, canEdit, fromDate, refreshRun, registerServerJob, resolvedToDate, selectedStores])

  const auditRunLabel = backfillAuditRunButtonLabel({
    auditMonth,
    hasAuditRows: allItems.length > 0,
    startedThisSession: auditMonth ? auditStartedMonths.has(auditMonth) : false,
    isRunning: isJobRunning,
  })

  const handleNoteBlur = useCallback(
    async (row: BackfillAuditListItem) => {
      if (!canEdit || row.decision === 'closed_year' || row.decision === 'applied') return
      const key = rowKey(row)
      const note = noteDrafts[key] ?? ''
      if (note === (row.decision_note ?? '')) return
      try {
        await updateBackfillAuditDecision(row.store_name, row.report_date, {
          decision: row.decision,
          decision_note: note,
        })
        setAllItems((current) =>
          current.map((item) =>
            rowKey(item) === key ? { ...item, decision_note: note } : item,
          ),
        )
      } catch (error) {
        toast.error(formatApiError(error, 'Failed to save note'))
      }
    },
    [canEdit, noteDrafts],
  )

  const handleDecisionChange = useCallback(
    async (row: BackfillAuditListItem, decision: BackfillAuditDecision) => {
      if (
        !canEdit ||
        row.decision === 'closed_year' ||
        row.decision === 'applied' ||
        decision === 'closed_year' ||
        decision === 'applied'
      ) {
        return
      }
      const key = rowKey(row)
      const previousDecision = row.decision
      setAllItems((current) =>
        current.map((item) => (rowKey(item) === key ? { ...item, decision } : item)),
      )
      try {
        await updateBackfillAuditDecision(row.store_name, row.report_date, {
          decision,
          decision_note: noteDrafts[key] ?? row.decision_note,
        })
      } catch (error) {
        setAllItems((current) =>
          current.map((item) =>
            rowKey(item) === key ? { ...item, decision: previousDecision } : item,
          ),
        )
        toast.error(formatApiError(error, 'Failed to update decision'))
      }
    },
    [canEdit, noteDrafts],
  )

  const handleExportCsv = useCallback(async () => {
    if (!fromDate || !resolvedToDate) return
    setIsExporting(true)
    try {
      await downloadBackfillAuditCsv({
        from: fromDate,
        to: resolvedToDate,
      })
      toast.success('CSV export started')
    } catch (error) {
      toast.error(formatApiError(error, 'CSV export failed'))
    } finally {
      setIsExporting(false)
    }
  }, [fromDate, resolvedToDate])

  const toggleExpand = useCallback(
    async (row: BackfillAuditListItem) => {
      const key = rowKey(row)
      if (expandedKey === key) {
        setExpandedKey(null)
        return
      }
      setExpandedKey(key)
      if (detailByKey[key]) return
      setDetailLoadingKey(key)
      try {
        const detail = await getBackfillAuditDetail(row.store_name, row.report_date)
        setDetailByKey((current) => ({ ...current, [key]: detail }))
      } catch (error) {
        toast.error(formatApiError(error, 'Failed to load row detail'))
        setExpandedKey(null)
      } finally {
        setDetailLoadingKey(null)
      }
    },
    [detailByKey, expandedKey],
  )

  const treeFiltered = useMemo(
    () => filterRowsWithTree(allItems, filterTree, filterFields),
    [allItems, filterFields, filterTree],
  )

  const textFiltered = useMemo(
    () =>
      filterRowsByText(treeFiltered, textSearch, (row) =>
        [
          row.report_date,
          row.store_name,
          row.status,
          row.categories.join(' '),
          row.diff_summary,
          row.decision,
          row.decision_note,
          row.qbo_doc_number,
          row.qbo_je_id,
        ]
          .filter(Boolean)
          .join(' '),
      ),
    [textSearch, treeFiltered],
  )

  const sortedRows = useMemo(
    () => applySorts(textFiltered, sortSpecs, FIELD_DEFINITIONS),
    [sortSpecs, textFiltered],
  )

  const filteredTotal = sortedRows.length
  const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE))

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return sortedRows.slice(start, start + PAGE_SIZE)
  }, [page, sortedRows])

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount)
    }
  }, [page, pageCount])

  const { getWidth, startResize, getStickyLeft } = useColumnWidths({
    pageKey: BACKFILL_AUDIT_PAGE_KEY,
    columnKeys: [...COLUMN_KEYS],
    defaults: DEFAULT_WIDTHS,
  })

  const pinnedKeys = useMemo(() => [...PINNED_COLUMNS], [])

  const visibleColumnKeys = useMemo(
    () =>
      COLUMN_KEYS.filter((key) => {
        if (key === 'expand') return true
        const vis = columnVisibility[key]
        return vis !== false
      }),
    [columnVisibility],
  )

  const tableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys],
  )

  const tableStyle = useMemo((): CSSProperties => {
    return {
      width: tableWidth,
      minWidth: tableWidth,
      tableLayout: 'fixed',
    }
  }, [tableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const isPinned = isBackfillAuditPinnedColumn(columnKey)
      const style: CSSProperties = {
        ...fixedColumnStyle(getWidth(columnKey)),
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys],
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const style: CSSProperties = fixedColumnStyle(getWidth(columnKey))
      if (isBackfillAuditPinnedColumn(columnKey)) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
        }
      }
      return style
    },
    [getStickyLeft, getWidth, pinnedKeys],
  )

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterFields, filterTree],
  )
  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])

  const summaryTotals = useMemo(() => {
    if (!summary?.months.length) return null
    let identical = 0
    let differs = 0
    let noActivity = 0
    let undecided = 0
    let giftCardDelta = 0
    for (const month of summary.months) {
      identical += month.identical
      differs += month.differs
      noActivity += month.no_activity ?? 0
      undecided += month.undecided
      const delta =
        typeof month.gift_card_delta === 'string'
          ? Number(month.gift_card_delta)
          : month.gift_card_delta
      if (Number.isFinite(delta)) giftCardDelta += delta
    }
    return { identical, differs, noActivity, undecided, giftCardDelta }
  }, [summary])

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
    setPage(1)
  }, [])

  const copyCatchUpText = useCallback(() => {
    if (!catchUp?.lines.length) return
    const rows = catchUp.lines.map((line) => {
      const debit =
        line.posting_type === 'Debit' ? formatBackfillMoney(line.amount).replace('$', '') : ''
      const credit =
        line.posting_type === 'Credit' ? formatBackfillMoney(line.amount).replace('$', '') : ''
      return [
        line.account_name,
        line.department ?? '',
        debit,
        credit,
        line.reason,
      ].join(' · ')
    })
    void navigator.clipboard.writeText(rows.join('\n'))
    toast.success('Copied catch-up lines to clipboard')
  }, [catchUp])

  const renderCell = (columnKey: BackfillAuditColumnKey, row: BackfillAuditListItem): ReactNode => {
    const style = cellStyle(columnKey)
    const pinnedClass = pinnedCellClass(columnKey)
    const key = rowKey(row)
    const isClosedYear = row.decision === 'closed_year' || row.report_date <= closedThrough
    const isApplied = row.decision === 'applied'

    switch (columnKey) {
      case 'expand':
        return (
          <td key={columnKey} style={style}>
            <button
              type="button"
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label={expandedKey === key ? 'Collapse row' : 'Expand row'}
              onClick={() => void toggleExpand(row)}
            >
              {expandedKey === key ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </td>
        )
      case 'report_date':
        return (
          <td key={columnKey} className={`tabular-nums ${pinnedClass}`} style={style}>
            {row.qbo_je_id ? (
              <JournalEntryExternalLink href={qboJournalUrl(row.qbo_je_id)}>
                {row.report_date}
              </JournalEntryExternalLink>
            ) : (
              row.report_date
            )}
          </td>
        )
      case 'store_name':
        return (
          <td key={columnKey} className={pinnedClass} style={style}>
            {row.store_name}
          </td>
        )
      case 'status':
        return (
          <td key={columnKey} style={style}>
            {row.status}
          </td>
        )
      case 'categories':
        return (
          <td key={columnKey} style={style}>
            <div className="flex flex-wrap gap-1">
              {row.categories.map((category) => (
                <span
                  key={category}
                  className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
                >
                  {category}
                </span>
              ))}
            </div>
          </td>
        )
      case 'delta_debits':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatBackfillAccountingMoney(row.delta_debits)}
          </td>
        )
      case 'diff_summary':
        return (
          <td key={columnKey} className="max-w-xs truncate" style={style} title={row.diff_summary ?? ''}>
            {row.diff_summary || formatDiffSummary([]) || '—'}
          </td>
        )
      case 'decision':
        return (
          <td key={columnKey} style={style}>
            <div className="flex items-center gap-1">
              {isClosedYear ? (
                <LockClosedIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
              ) : null}
              {isApplied ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                  {suggestedDecisionLabel('applied')}
                </span>
              ) : (
                <select
                  className="input-sm w-full min-w-[7rem]"
                  value={row.decision}
                  disabled={!canEdit || isClosedYear}
                  aria-label={`Decision for ${row.store_name} ${row.report_date}`}
                  onChange={(event) =>
                    void handleDecisionChange(row, event.target.value as BackfillAuditDecision)
                  }
                >
                  {decisionSelectOptions(row.decision).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </td>
        )
      case 'decision_note':
        return (
          <td key={columnKey} style={style}>
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
              value={noteDrafts[key] ?? row.decision_note ?? ''}
              disabled={!canEdit || isClosedYear || isApplied}
              onChange={(event) =>
                setNoteDrafts((current) => ({ ...current, [key]: event.target.value }))
              }
              onBlur={() => void handleNoteBlur(row)}
            />
          </td>
        )
      default:
        return (
          <td key={columnKey} style={style}>
            —
          </td>
        )
    }
  }

  const renderExpandedRow = (row: BackfillAuditListItem) => {
    const key = rowKey(row)
    const detail = detailByKey[key]
    const loading = detailLoadingKey === key
    const rebuiltLines =
      (detail?.rebuilt_payload?.lines as BackfillAuditNormLine[] | undefined) ?? []
    const changedKeys = new Set(
      (detail?.diff?.changed ?? []).map((entry) => entry.key ?? entry.account_name ?? ''),
    )

    return (
      <tr key={`${key}-detail`} className="bg-gray-50">
        <td colSpan={visibleColumnKeys.length} className="px-4 py-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading detail…</p>
          ) : !detail ? (
            <p className="text-sm text-gray-500">No detail available.</p>
          ) : (
            <div className="space-y-4">
              {(detail.rebuilt_warnings ?? []).map((warning) => (
                <WarningBanner key={warning} message={warning} />
              ))}
              {detail.deposit_window_probe ? (
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Deposit window probe:</span>{' '}
                  {JSON.stringify(detail.deposit_window_probe)}
                </div>
              ) : null}
              <BackfillAuditLineComparison
                beforeLines={detail.qbo_lines ?? []}
                afterLines={rebuiltLines}
                beforeTitle="QBO lines"
                afterTitle="Rebuilt lines"
                changedKeys={changedKeys}
              />
              {(detail.diff?.only_in_qbo?.length ?? 0) > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Only in QBO</h4>
                  <pre className="mt-1 overflow-x-auto text-xs text-gray-700">
                    {JSON.stringify(detail.diff?.only_in_qbo, null, 2)}
                  </pre>
                </div>
              ) : null}
              {(detail.diff?.only_in_rebuilt?.length ?? 0) > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Only in rebuilt</h4>
                  <pre className="mt-1 overflow-x-auto text-xs text-gray-700">
                    {JSON.stringify(detail.diff?.only_in_rebuilt, null, 2)}
                  </pre>
                </div>
              ) : null}
              {(detail.processor_rows?.length ?? 0) > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Processor rows</h4>
                  <pre className="mt-1 overflow-x-auto text-xs text-gray-700">
                    {JSON.stringify(detail.processor_rows, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="container-title">Run audit</h2>
        <p className="mt-1 text-xs text-gray-500">
          2025 rows are analysis-only (books closed); updatable range {bounds?.updatable_from ?? '2026-01-01'} →{' '}
          {bounds?.updatable_through ?? '…'}
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div className="backfill-audit-run-controls flex min-w-0 flex-1 flex-wrap items-end gap-4">
            <div className="min-w-[12rem]">
              <MultiSelectFilter
                id="backfill-audit-stores"
                label="Stores"
                options={STORE_OPTIONS}
                selected={selectedStores}
                onChange={setSelectedStores}
                placeholder="All stores"
              />
            </div>
            <div className="shrink-0">
              <label className="mb-1.5 block text-xs font-medium text-gray-600">Month</label>
              <ReportMonthSelect
                id="backfill-audit-month"
                options={auditMonthOptions}
                value={auditMonth}
                onChange={setAuditMonth}
                disabled={auditMonthOptions.length === 0}
                placeholder="Select month"
                className="w-[250px]"
              />
            </div>
            <div>
              <span className="mb-1.5 block text-xs font-medium text-gray-600">
                Refresh unchanged rows
              </span>
              <SegmentedToggle
                ariaLabel="Refresh unchanged rows"
                value={refreshRun ? 'refresh' : 'skip'}
                disabled={!canEdit}
                onChange={(value) => setRefreshRun(value === 'refresh')}
                options={[
                  { value: 'skip', label: 'Off' },
                  { value: 'refresh', label: 'On' },
                ]}
              />
            </div>
          </div>
          <Button
            variant="primary"
            gradient
            size="md"
            className="ml-auto shrink-0"
            disabled={!canEdit || isJobRunning || !auditMonth}
            onClick={() => void handleRun()}
          >
            {auditRunLabel}
          </Button>
        </div>
        {runJob ? (
          <p className="mt-3 text-sm text-gray-700">
            Job {runJob.id}: {cloverJobStatusLabel(runJob.status)}
            {runJob.total_items > 0
              ? ` · ${runJob.processed_items}/${runJob.total_items}`
              : runJob.status === 'pending'
                ? ' · queued'
                : ''}
            {runJob.error_message ? ` · ${runJob.error_message}` : ''}
          </p>
        ) : null}
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">Audit results</h2>
        </div>
        <CloverBackfillApplyCard
          canEdit={canEdit}
          reportMonth={auditMonth}
          summaryTotals={summaryTotals}
          monthDiffers={
            auditMonth
              ? (summary?.months.find((month) => month.month === auditMonth)?.differs ?? 0)
              : 0
          }
          auditRows={allItems}
          showDryRunButton={total > 0 && !isJobRunning && !listLoading}
          onApplySucceeded={() => void loadApplyMonths()}
        />
        {summary?.cutoff_disagrees ? (
          <WarningBanner
            className="mx-5 mt-3"
            message={`Inferred deposit cutoff (${summary.deposit_cutoff_inferred ?? 'unknown'}) disagrees with code cutoff ${summary.deposit_report_cutoff}.`}
          />
        ) : null}
        <TablePageShell className="px-0 py-0">
          <div className="space-y-2 border-b border-gray-100 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SearchInput
                value={textSearch}
                onChange={(value) => {
                  setTextSearch(value)
                  setPage(1)
                }}
                placeholder="Search rows…"
                className="max-w-md flex-1"
              />
              <TableToolbar
                filterCount={activeFilterCount}
                sortCount={sortSpecs.length}
                showFilters={showFilters}
                showSort={showSort}
                showColumns={showColumns}
                onToggleFilters={() => setShowFilters((open) => !open)}
                onToggleSort={() => setShowSort((open) => !open)}
                onToggleColumns={() => setShowColumns((open) => !open)}
                exportOptions={[
                  {
                    id: 'csv',
                    label: 'CSV',
                    onClick: () => void handleExportCsv(),
                    disabled: !fromDate || !resolvedToDate,
                  },
                ]}
                defaultExportId="csv"
                isExporting={isExporting}
                exportDisabled={!fromDate || !resolvedToDate}
              />
            </div>
          </div>
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={(pillId) => setFilterTree(removeFilterPill(filterTree, pillId))}
            onClearAll={() => setFilterTree(null)}
          />
          <div className="overflow-x-auto">
            <table
              className="table-forecast backfill-audit-table border-separate border-spacing-0"
              style={tableStyle}
            >
              <thead>
                <tr>
                  {visibleColumnKeys.map((columnKey) =>
                    isBackfillAuditColumnSortable(columnKey) ? (
                      <FixedAssetsSortableHeader
                        key={columnKey}
                        label={columnHeaderLabel(columnKey)}
                        sortKey={columnKey}
                        sortDirection={getSortDirection(columnKey)}
                        onSort={handleRequestSort}
                        columnKey={columnKey}
                        className={`${columnKey === 'delta_debits' ? 'text-right' : ''} ${pinnedHeaderClass(columnKey)}`.trim()}
                        style={headerStyle(columnKey)}
                        onStartResize={startResize}
                      />
                    ) : (
                      <th
                        key={columnKey}
                        className={`relative ${pinnedHeaderClass(columnKey)}`.trim()}
                        style={headerStyle(columnKey)}
                      >
                        {columnHeaderLabel(columnKey)}
                        {columnKey !== 'expand' ? (
                          <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
                        ) : null}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {listLoading && paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnKeys.length} className="py-8 text-center text-sm text-gray-500">
                      Loading audit rows…
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumnKeys.length} className="py-8 text-center text-sm text-gray-500">
                      No rows match the current filters.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.flatMap((row) => {
                    const key = rowKey(row)
                    const rows = [
                      <tr key={key} className="group">
                        {visibleColumnKeys.map((columnKey) => renderCell(columnKey, row))}
                      </tr>,
                    ]
                    if (expandedKey === key) rows.push(renderExpandedRow(row))
                    return rows
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            <span>
              {filteredTotal === 0
                ? 'No rows'
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filteredTotal)} of ${filteredTotal}`}
              {filteredTotal !== total ? ` (${total} total)` : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <span>Page {page} / {pageCount}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </TablePageShell>
        <FilterBuilderDrawer
          open={showFilters}
          onClose={() => setShowFilters(false)}
          fields={filterFields}
          root={filterTree}
          onChange={(tree) => {
            setFilterTree(tree)
            setPage(1)
          }}
          onClearAll={() => {
            setFilterTree(null)
            setPage(1)
          }}
        />
        <SortBuilder
          open={showSort}
          fields={FIELD_DEFINITIONS}
          sorts={sortSpecs}
          onChange={(sorts) => {
            setSortSpecs(sorts)
            setPage(1)
          }}
          onClose={() => setShowSort(false)}
          onClearAll={() => {
            setSortSpecs(DEFAULT_SORT)
            setPage(1)
          }}
        />
        <ColumnPicker
          open={showColumns}
          fields={FIELD_DEFINITIONS}
          visibility={columnVisibility}
          onChange={(visibility) => {
            setColumnVisibility(visibility)
            saveColumnVisibility(BACKFILL_AUDIT_PAGE_KEY, visibility)
          }}
          onClose={() => setShowColumns(false)}
          onReset={() => {
            const defaults = getDefaultColumnVisibility(FIELD_DEFINITIONS)
            setColumnVisibility(defaults)
            saveColumnVisibility(BACKFILL_AUDIT_PAGE_KEY, defaults)
          }}
        />
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setCatchUpOpen((open) => !open)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setCatchUpOpen((open) => !open)
            }
          }}
          className="flex cursor-pointer select-none items-start gap-2.5 border-b border-gray-200 px-5 py-4 hover:bg-gray-50"
          aria-expanded={catchUpOpen}
        >
          <ChevronDownIcon
            className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
              catchUpOpen ? '' : '-rotate-90'
            }`}
            aria-hidden="true"
          />
          <div>
            <h2 className="text-base font-semibold text-gray-900">2026 catch-up JE proposal</h2>
            <p className="mt-1 text-xs text-gray-500">Read-only preview for manual QuickBooks entry.</p>
          </div>
        </div>
        {catchUpOpen ? (
          <div className="space-y-4 p-5">
            {catchUpLoading ? (
              <p className="text-sm text-gray-500">Loading catch-up proposal…</p>
            ) : !catchUp ? (
              <p className="text-sm text-gray-500">Catch-up proposal unavailable.</p>
            ) : !catchUp.needed ? (
              <p className="text-sm text-gray-600">No 2026-01-01 catch-up JE needed</p>
            ) : (
              <>
                {!catchUp.balanced ? (
                  <WarningBanner message="Catch-up proposal is not balanced." />
                ) : null}
                {!catchUp.liability_nets_to_zero ? (
                  <WarningBanner message="Gift card liability residual does not net to zero." />
                ) : null}
                {!catchUp.prior_year_adjustment_mapped ? (
                  <WarningBanner message="Prior-year adjustment account is not mapped." />
                ) : null}
                {(catchUp.exclusions ?? []).map((item, index) => {
                  const text = [
                    item.store_name,
                    item.report_date,
                    item.status,
                    item.reason,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                  return (
                    <WarningBanner
                      key={`${item.store_name ?? 'row'}-${item.report_date ?? index}`}
                      message={`Excluded: ${text || 'row omitted from catch-up sum'}`}
                    />
                  )
                })}
                {(catchUp.warnings ?? []).map((item) => (
                  <WarningBanner key={item} message={item} />
                ))}
                <JournalEntryPreview
                  lines={proposalLinesToJournalEntry(catchUp.lines)}
                  variant="split"
                  hideHeader
                  editableAmounts={false}
                  editableLines={false}
                  editableLocations={false}
                />
                {catchUp.drivers.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Drivers</h3>
                    <table className="mt-2 w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-gray-500">
                          <th className="py-1">Driver</th>
                          <th className="py-1 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catchUp.drivers.map((driver, index) => (
                          <tr key={index} className="border-t border-gray-100">
                            <td className="py-1">{String(driver.reason ?? driver.label ?? index)}</td>
                            <td className="py-1 text-right tabular-nums">
                              {formatBackfillMoney(driver.amount as number | string | null | undefined)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {catchUp.dropped_pnl.length > 0 ? (
                  <div className="text-sm text-gray-500">
                    <div className="font-medium text-gray-700">Left in 2025 as closed</div>
                    <ul className="mt-1 list-disc pl-5">
                      {catchUp.dropped_pnl.map((entry, index) => (
                        <li key={entry.account_id ?? index}>
                          {entry.account_name ?? entry.account_id ?? 'Account'}{' '}
                          {formatBackfillMoney(entry.net)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <Button variant="outline" size="md" onClick={copyCatchUpText}>
                  Copy as text
                </Button>
              </>
            )}
          </div>
        ) : null}
      </section>
    </div>
  )
}
